import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";

/**
 * Landing page after Stripe Checkout success. Polls
 * GET /api/payments/status/{session_id} until payment_status is "paid"
 * (webhook-driven, but the endpoint also does a live Stripe fetch as a
 * fallback so we never wait forever). Once paid, we jump the customer to
 * the tokenised reservation page where the retailer address/contact are
 * unlocked. The reservation id + access token were stashed in sessionStorage
 * before the Stripe redirect.
 */
export default function ReservationPaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id") || "";
  const nav = useNavigate();
  const [phase, setPhase] = useState("waiting"); // "waiting" | "paid" | "failed"
  const [attempts, setAttempts] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!sid) {
      setPhase("failed");
      return;
    }
    cancelled.current = false;
    let timer;

    async function poll(n) {
      if (cancelled.current) return;
      setAttempts(n);
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setPhase("paid");
          // Look up stashed access token
          let stash = null;
          try {
            const raw = window.sessionStorage.getItem(`fm.res.${sid}`);
            stash = raw ? JSON.parse(raw) : null;
          } catch (err) {
            // Corrupt/unavailable storage — fall back to the email-only flow.
            console.warn("FloorMatch: couldn't read stashed reservation token; using email-only fallback.", err);
          }
          const reservationId = r.data.reservation_id || stash?.reservation_id;
          const token = stash?.access_token;
          setTimeout(() => {
            if (cancelled.current) return;
            if (reservationId && token) {
              nav(`/reservation/${reservationId}?t=${encodeURIComponent(token)}`);
            } else {
              // Fallback — customer must use the confirmation email link.
              setPhase("email-only");
            }
          }, 900);
          return;
        }
        if (
          r.data.payment_status === "expired" ||
          r.data.payment_status === "async_payment_failed" ||
          r.data.status === "failed"
        ) {
          setPhase("failed");
          return;
        }
      } catch (e) {
        // Transient — keep polling until we hit the retry cap.
      }
      if (n >= 20) {
        setPhase("email-only");
        return;
      }
      timer = setTimeout(() => poll(n + 1), 1500);
    }
    poll(1);
    return () => {
      cancelled.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [sid, nav]);

  return (
    <>
      <PublicHeader />
      <main className="min-h-screen" data-testid="reservation-payment-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
          {phase === "waiting" && (
            <div data-testid="payment-waiting">
              <Loader2 className="w-10 h-10 text-emerald-800 mx-auto animate-spin" />
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-4">
                Confirming your payment…
              </h1>
              <p className="text-slate-600 mt-2 text-sm">
                Waiting on Stripe. This usually takes a second or two.
                {attempts > 6 && " (Still waiting — hang tight.)"}
              </p>
            </div>
          )}
          {phase === "paid" && (
            <div data-testid="payment-paid">
              <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto" />
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-4">
                Payment received
              </h1>
              <p className="text-slate-600 mt-2 text-sm">
                Opening your collection details…
              </p>
            </div>
          )}
          {phase === "email-only" && (
            <div data-testid="payment-check-email">
              <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto" />
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-4">
                Payment received
              </h1>
              <p className="text-slate-600 mt-2 text-sm">
                We've emailed your collection details. The link in that email
                is the only way to see the retailer's address.
              </p>
              <Link
                to="/search"
                className="fm-btn-primary inline-block mt-6"
                data-testid="back-to-search"
              >
                Back to search
              </Link>
            </div>
          )}
          {phase === "failed" && (
            <div data-testid="payment-failed">
              <AlertCircle className="w-10 h-10 text-red-600 mx-auto" />
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-4">
                Payment didn't go through
              </h1>
              <p className="text-slate-600 mt-2 text-sm">
                No charge was made. The remnant has been released back to the
                marketplace, so you can try again from the listing.
              </p>
              <Link
                to="/search"
                className="fm-btn-primary inline-block mt-6"
                data-testid="back-to-search"
              >
                Back to search
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
